/**
 * React Query hooks for the workspace + brand API. All requests go
 * through the authed `api()` wrapper (Cognito ID token attached).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ApiError } from "./api";
import { loadTokens } from "./auth";
import type { BrandBrain } from "@notamen/core/brand";
import type {
  CompDurationUnit,
  CompGrant,
  CompPlanId,
  CompType,
  Invite,
  Membership,
} from "@notamen/core/workspace";
import type { Cadence, MediaBillingMode, PlanId } from "@notamen/core/billing";
import type {
  BrandScanJobRecord,
  BrandVersionRecord,
  DraftRecord,
  GenerationJobRecord,
  DraftProvider,
  InputRecord,
  PatternRecord,
  PatternStatus,
  MediaGenJobStatus,
  MediaRecord,
  ProductRecord,
  ProductSyncSummaryRecord,
  TrendClusterRecord,
  TrendConfigRecord,
  TrendDigestRecord,
  TrendScanJobRecord,
  TrendSourceAccountRecord,
  TrendTopicRecord,
} from "@notamen/core/db";
import type {
  ContentKind,
  MediaAspect,
  MediaPurpose,
  Platform,
  ShotMode,
  TrendSourceConfig,
  TrendSourceKind,
} from "@notamen/core/types";

export type WorkspaceRole = "owner" | "admin" | "editor" | "approver" | "viewer";

// Complimentary-access (demo/trial) types, re-exported from core so SPA
// surfaces (admin grant UI, the lock wall/banner) import them from one place.
export type { CompGrant, CompType, CompPlanId, CompDurationUnit } from "@notamen/core/workspace";
// Subscription summary type, re-exported from core for the billing UI + the
// access gate (which walls a canceled subscription, same as an expired comp).
export type { SubscriptionSummary } from "@notamen/core/billing";

export interface Workspace {
  id: string;
  name: string;
  plan: string;
  createdAt: string;
  createdBy: string;
  status?: "pending-approval" | "active";
  /**
   * Complimentary demo/trial grant set by a platform admin. Present only
   * while a grant is (or was) active; absent on normal paying/free
   * workspaces. The SPA derives lock/active state via
   * `compStatus(workspace.comp)` from `@notamen/core/workspace`.
   */
  comp?: CompGrant;
  /**
   * Self-serve Stripe subscription summary, denormalized by the billing
   * webhook. Absent on never-subscribed (free) workspaces. The access gate
   * walls the workspace when `subscriptionStatus(subscription).locked` (a
   * terminally canceled subscription), mirroring the comp-lock wall.
   */
  subscription?: import("@notamen/core/billing").SubscriptionSummary;
}

export interface BrandSummary extends BrandBrain {
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Workspaces ───────────────────────────────────────────────────────

export function useWorkspaces() {
  return useQuery<{ workspaces: Workspace[] }, ApiError>({
    queryKey: ["workspaces"],
    queryFn: () => api<{ workspaces: Workspace[] }>("/workspaces"),
  });
}

export function useWorkspace(wsId: string | undefined) {
  return useQuery<{ workspace: Workspace; role: WorkspaceRole }, ApiError>({
    queryKey: ["workspace", wsId],
    queryFn: () => api<{ workspace: Workspace; role: WorkspaceRole }>(`/workspaces/${wsId}`),
    enabled: !!wsId,
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation<{ workspace: Workspace }, ApiError, { name: string }>({
    mutationFn: (body) =>
      api<{ workspace: Workspace }>("/workspaces", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

/** Rename a workspace (owner only, enforced server-side). */
export function useUpdateWorkspace(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ workspace: Workspace }, ApiError, { name: string }>({
    mutationFn: (body) =>
      api<{ workspace: Workspace }>(`/workspaces/${wsId}`, { method: "PATCH", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["workspace", wsId] });
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

/** Delete a workspace (owner only, enforced server-side). */
export function useDeleteWorkspace(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, void>({
    mutationFn: () => api<{ ok: true }>(`/workspaces/${wsId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["workspace", wsId] });
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

/** Leave a workspace — removes the caller's own membership. */
export function useLeaveWorkspace(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, void>({
    mutationFn: () =>
      api<{ ok: true }>(`/workspaces/${wsId}/members/@me`, { method: "DELETE" }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["workspace", wsId] });
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

// ─── Members ──────────────────────────────────────────────────────────

export type { Membership } from "@notamen/core/workspace";

export function useMembers(wsId: string | undefined) {
  return useQuery<{ members: Membership[] }, ApiError>({
    queryKey: ["members", wsId],
    queryFn: () => api<{ members: Membership[] }>(`/workspaces/${wsId}/members`),
    enabled: !!wsId,
  });
}

export function useUpdateMember(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { member: Membership },
    ApiError,
    { userId: string; role?: Membership["role"]; brandScope?: string[] }
  >({
    mutationFn: ({ userId, ...body }) =>
      api<{ member: Membership }>(`/workspaces/${wsId}/members/${userId}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["members", wsId] });
    },
  });
}

export function useRemoveMember(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { userId: string }>({
    mutationFn: ({ userId }) =>
      api<{ ok: true }>(`/workspaces/${wsId}/members/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["members", wsId] });
    },
  });
}

// ─── Enterprise SAML SSO (per-workspace) ─────────────────────────────
// Owner-only on enterprise-plan workspaces; server-side gate in saml.ts.
// `useSsoSaml` returns `{ config, sp }`. `config` is null when nothing
// is configured yet; `sp` is the always-derivable SP metadata the IdP
// team needs to register on their side (entity id + ACS URL).

export interface SamlSp {
  entityId: string;
  acsUrl: string | null;
  audience: string;
}

export interface SamlConfig {
  workspaceId: string;
  providerName: string;
  idpMetadataUrl?: string;
  idpMetadataXml?: string;
  attributeMapping: Record<string, string>;
  status: "active" | "disabled";
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

export interface SamlPayload {
  config: SamlConfig | null;
  sp: SamlSp;
}

export function useSsoSaml(wsId: string | undefined) {
  return useQuery<SamlPayload, ApiError>({
    queryKey: ["sso-saml", wsId],
    queryFn: () => api<SamlPayload>(`/workspaces/${wsId}/sso/saml`),
    enabled: !!wsId,
  });
}

/**
 * Upsert the workspace's SAML config. Body must provide exactly one of
 * `idpMetadataUrl` (preferred; Cognito refreshes it) or `idpMetadataXml`
 * (raw paste). `attributeMapping` is optional; defaults applied server-
 * side. Invalidates the GET query so the form re-renders with the
 * canonical server state.
 */
export function usePutSsoSaml(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    SamlPayload,
    ApiError,
    {
      idpMetadataUrl?: string;
      idpMetadataXml?: string;
      attributeMapping?: Record<string, string>;
    }
  >({
    mutationFn: (body) =>
      api<SamlPayload>(`/workspaces/${wsId}/sso/saml`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sso-saml", wsId] });
    },
  });
}

export interface SamlLookup {
  hasSaml: boolean;
  providerName?: string;
}

/**
 * Public (unauthenticated) lookup used by `/signin/:wsId` to decide
 * whether to render a "Continue with SSO" button for that workspace.
 * Backed by `GET /api/auth/workspace/<wsId>/saml` — short-lived stale
 * tolerance is fine since IdP config changes are rare and the user
 * lands here from a deliberate URL.
 */
export function useSamlLookup(wsId: string | undefined) {
  return useQuery<SamlLookup, ApiError>({
    queryKey: ["sso-saml-lookup", wsId],
    // `unauthenticated: true` — the slug-signin page renders BEFORE the
    // user has a JWT, so skip the auth header entirely (sending a stale
    // expired token would 401, and the lookup is server-side public
    // anyway).
    queryFn: () => api<SamlLookup>(`/auth/workspace/${wsId}/saml`, { unauthenticated: true }),
    enabled: !!wsId,
    staleTime: 60 * 1000,
  });
}

// ─── SAML SSO email-domain claims (per-workspace, DNS-verified) ─────

export interface DomainRegistration {
  workspaceId: string;
  domain: string;
  status: "pending" | "verified";
  verificationToken: string;
  createdAt: string;
  createdBy: string;
  verifiedAt?: string;
  updatedAt: string;
}

export function useSsoDomains(wsId: string | undefined) {
  return useQuery<{ domains: DomainRegistration[] }, ApiError>({
    queryKey: ["sso-domains", wsId],
    queryFn: () => api<{ domains: DomainRegistration[] }>(
      `/workspaces/${wsId}/sso/domains`,
    ),
    enabled: !!wsId,
  });
}

export function useCreateSsoDomain(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { domain: DomainRegistration },
    ApiError,
    { domain: string }
  >({
    mutationFn: (body) =>
      api<{ domain: DomainRegistration }>(`/workspaces/${wsId}/sso/domains`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sso-domains", wsId] });
    },
  });
}

export function useVerifySsoDomain(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { domain: DomainRegistration },
    ApiError,
    { domain: string }
  >({
    mutationFn: ({ domain }) =>
      api<{ domain: DomainRegistration }>(
        `/workspaces/${wsId}/sso/domains/${encodeURIComponent(domain)}/verify`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sso-domains", wsId] });
    },
  });
}

export function useDeleteSsoDomain(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { domain: string }>({
    mutationFn: ({ domain }) =>
      api<{ ok: true }>(
        `/workspaces/${wsId}/sso/domains/${encodeURIComponent(domain)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sso-domains", wsId] });
    },
  });
}

// ─── Social publish accounts (per-brand OAuth connections) ─────────

/**
 * Non-secret metadata for a connected publish account. The OAuth tokens
 * themselves live in SSM SecureString server-side and never reach the
 * client — only handle / status / expiry come back here.
 */
export interface SocialAccountSummary {
  platform: Platform;
  handle: string;
  status: "connected" | "expired" | "revoked";
  connectedAt: string;
  expiresAt?: string;
}

export function useSocialAccounts(ws?: string, brand?: string) {
  return useQuery<{ accounts: SocialAccountSummary[] }, ApiError>({
    queryKey: ["social-accounts", ws, brand],
    queryFn: () =>
      api<{ accounts: SocialAccountSummary[] }>(
        `/workspaces/${ws}/brands/${brand}/accounts`,
      ),
    enabled: !!ws && !!brand,
  });
}

/**
 * Start the OAuth connect flow for a platform. Returns either an
 * `authorizeUrl` (the component navigates the browser to it) or a
 * `not_configured` result (an expected state — the deployment has no
 * OAuth app for this platform; surface the message inline, not as an
 * error). Owner/admin only server-side.
 */
export function useConnectAccount(ws?: string, brand?: string) {
  const qc = useQueryClient();
  return useMutation<
    { authorizeUrl: string } | { error: "not_configured"; message: string },
    ApiError,
    { platform: string }
  >({
    mutationFn: ({ platform }) =>
      api<{ authorizeUrl: string } | { error: "not_configured"; message: string }>(
        `/workspaces/${ws}/brands/${brand}/accounts/${platform}/connect`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["social-accounts", ws, brand] });
    },
  });
}

/** Disconnect a connected publish account. Owner/admin only server-side. */
export function useDisconnectAccount(ws?: string, brand?: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { platform: string }>({
    mutationFn: ({ platform }) =>
      api<{ ok: true }>(
        `/workspaces/${ws}/brands/${brand}/accounts/${platform}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["social-accounts", ws, brand] });
    },
  });
}

/**
 * Public (unauthenticated) domain → workspace discovery. The sign-in
 * page splits the typed email client-side and sends ONLY the domain
 * to the server — the full email never lands in API Gateway /
 * CloudWatch / CloudFront access logs. Returns ONLY
 * `{hasSaml, workspaceId?}` — see saml.ts for the privacy guarantees.
 */
export interface DiscoverResult {
  hasSaml: boolean;
  workspaceId?: string;
}
export async function discoverByEmailDomain(email: string): Promise<DiscoverResult> {
  // Defensive split — return a no-op result for inputs that aren't yet
  // a usable email shape so we don't fire a doomed request.
  const at = email.lastIndexOf("@");
  if (at <= 0 || at >= email.length - 1) return { hasSaml: false };
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (!domain) return { hasSaml: false };
  return api<DiscoverResult>(
    `/auth/discover?domain=${encodeURIComponent(domain)}`,
    { unauthenticated: true },
  );
}

/** Delete the workspace's Cognito IdP + the persisted row. */
export function useDeleteSsoSaml(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError>({
    mutationFn: () =>
      api<{ ok: true }>(`/workspaces/${wsId}/sso/saml`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sso-saml", wsId] });
    },
  });
}

// ─── Personal 2FA (software-token MFA, self-service) ─────────────────

/** Authoritative read from Cognito of the caller's TOTP enrolment. */
export function useTwoFactorStatus() {
  return useQuery<{ enabled: boolean }, ApiError>({
    queryKey: ["me", "2fa", "status"],
    queryFn: () => api<{ enabled: boolean }>("/me/2fa/status"),
    // Don't refetch in the background — the user's MFA state only changes
    // when they enrol or disable from this same surface.
    staleTime: Infinity,
  });
}

/**
 * Begin TOTP enrolment: returns the base32 secret to render in a QR code.
 * Forwards the access token Cognito needs for AssociateSoftwareToken.
 */
export function useStart2fa() {
  return useMutation<{ secretCode: string }, ApiError>({
    mutationFn: () => {
      const accessToken = loadTokens()?.accessToken;
      return api<{ secretCode: string }>("/me/2fa/start", {
        method: "POST",
        ...(accessToken ? { accessToken } : {}),
      });
    },
  });
}

/** Verify a TOTP code and enable MFA. Invalidates members + 2FA status. */
export function useVerify2fa() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { code: string }>({
    mutationFn: ({ code }) => {
      const accessToken = loadTokens()?.accessToken;
      return api<{ ok: true }>("/me/2fa/verify", {
        method: "POST",
        body: { code },
        ...(accessToken ? { accessToken } : {}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["me", "2fa", "status"] });
      void qc.invalidateQueries({ queryKey: ["members"] });
    },
  });
}

/** Disable software-token MFA. Invalidates members + 2FA status. */
export function useDisable2fa() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError>({
    mutationFn: () => {
      const accessToken = loadTokens()?.accessToken;
      return api<{ ok: true }>("/me/2fa/disable", {
        method: "POST",
        ...(accessToken ? { accessToken } : {}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["me", "2fa", "status"] });
      void qc.invalidateQueries({ queryKey: ["members"] });
    },
  });
}

// ─── Admin operator console (sm-admins, cross-tenant) ─────────────────

export interface AdminOverview {
  workspaces: number;
  pendingApproval: number;
  users: number;
  brands: number;
  drafts: number;
}

export interface AdminTenant {
  id: string;
  name: string;
  plan: PlanId;
  status: "pending-approval" | "active";
  createdAt: string;
  brandCount: number;
  memberCount: number;
  /**
   * Complimentary demo/trial grant on this workspace, when present. Drives
   * the comp badge + countdown and the Grant/Revoke controls on the tenant
   * row. Absent on workspaces with no comp.
   */
  comp?: CompGrant;
}

/** Cross-tenant operator summary (Overview counts + Tenants list) from one
 *  bounded scan. sm-admins only (server-enforced). Feeds both admin tabs. */
export function useAdminOverview() {
  return useQuery<{ overview: AdminOverview; tenants: AdminTenant[] }, ApiError>({
    queryKey: ["admin-overview"],
    queryFn: () => api<{ overview: AdminOverview; tenants: AdminTenant[] }>("/admin/overview"),
  });
}

/**
 * Admin-only: set a workspace's plan. Invalidates the admin overview so
 * the tenants list re-renders with the new value. Used by the Plan
 * column dropdown in `/admin`.
 */
export function useSetTenantPlan() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; workspaceId: string; plan: PlanId },
    ApiError,
    { workspaceId: string; plan: PlanId }
  >({
    mutationFn: ({ workspaceId, plan }) =>
      api(`/admin/workspaces/${workspaceId}/plan`, {
        method: "POST",
        body: { plan },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
  });
}

/**
 * Admin-only: grant a complimentary demo/trial to a workspace. Sets
 * `workspace.plan` to the granted plan for the duration, then locks the
 * workspace when it expires. `plan` is optional — the server defaults it
 * from the comp type (COMP_DEFAULT_PLAN) when omitted. Invalidates the
 * admin overview so the tenant row re-renders with the new grant.
 */
export function useSetTenantComp() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; workspaceId: string; comp: CompGrant; plan: PlanId },
    ApiError,
    {
      workspaceId: string;
      type: CompType;
      plan?: CompPlanId;
      durationValue: number;
      durationUnit: CompDurationUnit;
    }
  >({
    mutationFn: ({ workspaceId, ...body }) =>
      api(`/admin/workspaces/${workspaceId}/comp`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
  });
}

/**
 * Admin-only: revoke a workspace's complimentary grant, restoring its
 * previous plan. Invalidates the admin overview so the badge clears.
 */
export function useClearTenantComp() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; workspaceId: string; plan: PlanId },
    ApiError,
    { workspaceId: string }
  >({
    mutationFn: ({ workspaceId }) =>
      api(`/admin/workspaces/${workspaceId}/comp`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
  });
}

export interface CostByTenant {
  workspaceId: string;
  name: string;
  spendUsd: number;
}

export interface CostByModel {
  provider: "anthropic" | "bedrock";
  modelId: string;
  label: string;
  spendUsd: number;
  tokens: number;
}

export interface CacheHealthPoint {
  hour: string;
  hitRate: number | null;
  tokens: number;
}

export interface AdminCost {
  spend7dUsd: number;
  spend30dUsd: number;
  cacheHitRate: number | null;
  avgTokensPerDraft: number | null;
  draftCount30d: number;
  spendByTenant: CostByTenant[];
  providerMix: { anthropicUsd: number; bedrockUsd: number; byModel: CostByModel[] };
  cacheHealth: CacheHealthPoint[];
  windowDays: number;
  /** Platform-wide AWS 30-day estimate; null when Cost Explorer was unavailable. */
  infraCost30dUsd: number | null;
  infraCostError?: string;
}

/** Cross-tenant AI spend + AWS infra estimate for the admin Cost tab.
 *  sm-admins only (server-enforced). Cached for 5 minutes because the backend
 *  does a full-table scan plus a (~$0.01) Cost Explorer call per fetch. */
export function useAdminCost() {
  return useQuery<AdminCost, ApiError>({
    queryKey: ["admin-cost"],
    queryFn: () => api<AdminCost>("/admin/cost"),
    staleTime: 5 * 60_000,
  });
}

// ─── Admin billing ────────────────────────────────────────────────────

export interface BillingTenant {
  workspaceId: string;
  name: string;
  plan: PlanId;
  cadence: "monthly" | "annual";
  mrrUsd: number;
}

export interface AdminBilling {
  mrrUsd: number;
  arrUsd: number;
  activeSubscriptions: number;
  tenantsByMrr: BillingTenant[];
}

/** Cross-tenant billing rollup (MRR/ARR + tenants by MRR) for the admin
 *  Billing tab. sm-admins only (server-enforced). Computed from the same
 *  bounded scan as overview; cached 5 min. */
export function useAdminBilling() {
  return useQuery<AdminBilling, ApiError>({
    queryKey: ["admin-billing"],
    queryFn: () => api<AdminBilling>("/admin/billing"),
    staleTime: 5 * 60_000,
  });
}

export interface BillingFlowCount {
  count: number;
  mrrUsd: number;
}
export interface BillingRenewal {
  workspaceId: string | null;
  name: string | null;
  plan: PlanId;
  cadence: "monthly" | "annual";
  amountUsd: number;
  renewsAt: string;
}
export interface BillingPaymentRow {
  workspaceId: string | null;
  name: string | null;
  amountUsd: number;
  currency: string;
  status: string;
  at: string;
}
export type BillingSubsSection =
  | { ok: true; newSubs: BillingFlowCount; churned: BillingFlowCount; renewals: BillingRenewal[] }
  | { ok: false; reason: "unconfigured" | "error"; message?: string };
export type BillingPaymentsSection =
  | { ok: true; items: BillingPaymentRow[] }
  | { ok: false; reason: "unconfigured" | "error"; message?: string };
export interface AdminBillingFlows {
  subscriptions: BillingSubsSection;
  payments: BillingPaymentsSection;
  renewalWindowDays: number;
  paymentWindowDays: number;
}

/** Stripe-backed Billing sections (new/churned subs, renewals, payments) for
 *  the admin Billing tab. sm-admins only. SEPARATE from useAdminBilling so the
 *  instant MRR tile never blocks on (or breaks with) a slow/down Stripe.
 *  Cached 5 min — it makes live Stripe calls + reverse-map reads. */
export function useAdminBillingFlows() {
  return useQuery<AdminBillingFlows, ApiError>({
    queryKey: ["admin-billing-flows"],
    queryFn: () => api<AdminBillingFlows>("/admin/billing/flows"),
    staleTime: 5 * 60_000,
  });
}

// ─── Admin audit log ──────────────────────────────────────────────────

export type AdminAuditAction =
  | "set_plan"
  | "grant_comp"
  | "revoke_comp"
  | "approve_waitlist"
  | "reject_waitlist";

export interface AdminAuditEvent {
  action: AdminAuditAction;
  actorSub: string;
  actorEmail?: string;
  targetWorkspaceId?: string;
  targetUsername?: string;
  summary: string;
  createdAt: string;
}

/** Operator audit log for the last 7 days, newest-first. sm-admins only
 *  (server-enforced). Cached 1 min — it's a small bounded per-day Query set. */
export function useAdminAudit() {
  return useQuery<{ events: AdminAuditEvent[]; windowDays: number }, ApiError>({
    queryKey: ["admin-audit"],
    queryFn: () =>
      api<{ events: AdminAuditEvent[]; windowDays: number }>("/admin/audit"),
    staleTime: 60_000,
  });
}

// ─── Invites ──────────────────────────────────────────────────────────

export type { Invite } from "@notamen/core/workspace";

export function useInvites(wsId: string | undefined) {
  return useQuery<{ invites: Invite[] }, ApiError>({
    queryKey: ["invites", wsId],
    queryFn: () => api<{ invites: Invite[] }>(`/workspaces/${wsId}/invites`),
    enabled: !!wsId,
  });
}

export interface CreateInviteVars {
  email: string;
  role: Invite["role"];
  brandScope?: string[];
}

export function useCreateInvite(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ invite: Invite }, ApiError, CreateInviteVars>({
    mutationFn: (body) =>
      api<{ invite: Invite }>(`/workspaces/${wsId}/invites`, { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invites", wsId] });
    },
  });
}

export function useRevokeInvite(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { token: string }>({
    mutationFn: ({ token }) =>
      api<{ ok: true }>(`/workspaces/${wsId}/invites/${encodeURIComponent(token)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invites", wsId] });
    },
  });
}

/** Extend a copy-link invite's expiry by another window, keeping the link. */
export function useRenewInvite(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ invite: Invite }, ApiError, { token: string }>({
    mutationFn: ({ token }) =>
      api<{ invite: Invite }>(
        `/workspaces/${wsId}/invites/${encodeURIComponent(token)}/renew`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invites", wsId] });
    },
  });
}

export function useAcceptInvite(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { membership: Membership; alreadyMember?: boolean },
    ApiError,
    { token: string }
  >({
    mutationFn: ({ token }) =>
      api<{ membership: Membership; alreadyMember?: boolean }>(
        `/workspaces/${wsId}/invites/${encodeURIComponent(token)}/accept`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["workspaces"] });
      void qc.invalidateQueries({ queryKey: ["members", wsId] });
    },
  });
}

// ─── Brands ───────────────────────────────────────────────────────────

export function useBrands(wsId: string | undefined) {
  return useQuery<{ brands: BrandSummary[] }, ApiError>({
    queryKey: ["brands", wsId],
    queryFn: () => api<{ brands: BrandSummary[] }>(`/workspaces/${wsId}/brands`),
    enabled: !!wsId,
  });
}

export function useBrand(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<{ brand: BrandSummary }, ApiError>({
    queryKey: ["brand", wsId, brandId],
    queryFn: () => api<{ brand: BrandSummary }>(`/workspaces/${wsId}/brands/${brandId}`),
    enabled: !!wsId && !!brandId,
  });
}

export interface CreateBrandInput {
  name: string;
  kind: BrandBrain["kind"];
  elevator: string;
  audiencePrimary: string;
}

export function useCreateBrand(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ brand: BrandSummary }, ApiError, CreateBrandInput>({
    mutationFn: (body) =>
      api<{ brand: BrandSummary }>(`/workspaces/${wsId}/brands`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["brands", wsId] });
    },
  });
}

export function useUpdateBrand(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ brand: BrandSummary }, ApiError, BrandBrain>({
    mutationFn: (brain) =>
      api<{ brand: BrandSummary }>(`/workspaces/${wsId}/brands/${brandId}`, {
        method: "PUT",
        body: brain,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["brand", wsId, brandId], data);
      void qc.invalidateQueries({ queryKey: ["brands", wsId] });
    },
  });
}

// ─── Products ─────────────────────────────────────────────────────────

export type { ProductRecord, ProductSyncSummaryRecord } from "@notamen/core/db";

export interface ProductsResponse {
  products: ProductRecord[];
  summary: ProductSyncSummaryRecord | null;
}

export function useProducts(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<ProductsResponse, ApiError>({
    queryKey: ["products", wsId, brandId],
    queryFn: () => api<ProductsResponse>(`/workspaces/${wsId}/brands/${brandId}/products`),
    enabled: !!wsId && !!brandId,
  });
}

/**
 * Trigger an on-demand product sync. The worker runs async, so on success
 * we refetch the products list a couple of times to surface results as
 * they land (without making the user reload).
 */
export function useSyncProducts(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ status: string }, ApiError, void>({
    mutationFn: () =>
      api<{ status: string }>(`/workspaces/${wsId}/brands/${brandId}/sync-products`, {
        method: "POST",
      }),
    onSuccess: () => {
      const refetch = () => void qc.invalidateQueries({ queryKey: ["products", wsId, brandId] });
      setTimeout(refetch, 6000);
      setTimeout(refetch, 15000);
    },
  });
}

export interface UpdateProductGalleryVars {
  handle: string;
  galleryMediaIds: string[];
}

/**
 * Set a product's linked media gallery (the photos the generator can pull as
 * references). Server validates the media ids belong to this brand and drops
 * unknowns, so the returned product is the source of truth for what stuck.
 */
export function useUpdateProductGallery(
  wsId: string | undefined,
  brandId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation<{ product: ProductRecord }, ApiError, UpdateProductGalleryVars>({
    mutationFn: ({ handle, galleryMediaIds }) =>
      api<{ product: ProductRecord }>(
        `/workspaces/${wsId}/brands/${brandId}/products/${encodeURIComponent(handle)}`,
        { method: "PATCH", body: { galleryMediaIds } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products", wsId, brandId] });
    },
  });
}

// ─── Media library ────────────────────────────────────────────────────

export type { MediaRecord } from "@notamen/core/db";

/** A media record plus a presigned GET url (null when not yet available). */
export type MediaItem = MediaRecord & { url: string | null };

/** Derive the media id from a record's `sk` (`MEDIA#<id>`). */
export function mediaId(media: MediaRecord): string {
  return media.sk.replace(/^MEDIA#/, "");
}

export function useMedia(
  wsId: string | undefined,
  brandId: string | undefined,
  purpose?: MediaPurpose,
) {
  return useQuery<{ media: MediaItem[] }, ApiError>({
    queryKey: ["media", wsId, brandId, purpose ?? "all"],
    queryFn: () =>
      api<{ media: MediaItem[] }>(
        `/workspaces/${wsId}/brands/${brandId}/media${purpose ? `?purpose=${purpose}` : ""}`,
      ),
    enabled: !!wsId && !!brandId,
  });
}

export interface CreateMediaVars {
  file: File;
  purpose: MediaPurpose;
  description?: string;
}

/**
 * Two-step upload: POST to create the record + get a presigned PUT url, then
 * PUT the raw file bytes directly to S3 (NOT through the api() wrapper — it's
 * an unauthenticated S3 URL and must not carry the Cognito bearer token).
 */
export function useCreateMedia(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ media: MediaRecord }, ApiError, CreateMediaVars>({
    mutationFn: async ({ file, purpose, description }) => {
      const { media, uploadUrl } = await api<{ media: MediaRecord; uploadUrl: string }>(
        `/workspaces/${wsId}/brands/${brandId}/media`,
        {
          method: "POST",
          body: {
            mime: file.type,
            purpose,
            ...(description ? { description } : {}),
          },
        },
      );
      const res = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!res.ok) {
        throw new Error(`Upload failed (${res.status})`);
      }
      return { media };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["media", wsId, brandId] });
    },
  });
}

export interface UpdateMediaVars {
  id: string;
  purpose?: MediaPurpose;
  description?: string;
  tags?: string[];
  approvedForUse?: boolean;
}

export function useUpdateMedia(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ media: MediaRecord }, ApiError, UpdateMediaVars>({
    mutationFn: ({ id, ...patch }) =>
      api<{ media: MediaRecord }>(`/workspaces/${wsId}/brands/${brandId}/media/${id}`, {
        method: "PATCH",
        body: patch,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["media", wsId, brandId] });
    },
  });
}

export function useDeleteMedia(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      api<{ ok: true }>(`/workspaces/${wsId}/brands/${brandId}/media/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["media", wsId, brandId] });
    },
  });
}

// ─── Media generation (real photo/video) ──────────────────────────────
//
// Drives the PR2 media-gen API: POST enqueues a job (server runs a read-only
// billing pre-check, then async-invokes the generate-media worker), GET polls
// it. The worker is the only place that meters usage, on terminal success — the
// client never computes or sends a cost. `usageUnits` shown in the UI come from
// the @notamen/core/media-models registry and are INFORMATIONAL only.

/** image | video — the two media-generation capabilities (mirrors the API). */
export type MediaGenCapability = "image" | "video";

/** POST body for a media-gen job (see api/media-gen.ts createMediaGenJob). */
export interface CreateMediaGenVars {
  capability: MediaGenCapability;
  modelId: string;
  shotMode: ShotMode;
  prompt: string;
  negativePrompt?: string;
  aspect?: MediaAspect;
  /** Product handles whose imagery the worker composites into the shot. */
  productHandles?: string[];
  /**
   * Specific catalog images chosen per product — each a (handle, index) into
   * the product's sourceImageUrls. Lets the picker feature more than one photo
   * per product.
   */
  productImages?: { handle: string; index: number }[];
  /** Uploaded media (MediaRecord sha ids) used as references/inputs. */
  sourceMediaIds?: string[];
  /** When set, the finished asset is attached to this draft on completion. */
  draftId?: string;
}

/** GET /media-gen/{jobId} response (see api/media-gen.ts getMediaGenJobHandler). */
export interface MediaGenJobStatusResponse {
  jobId: string;
  status: MediaGenJobStatus;
  resultMediaIds: string[];
  error?: string;
}

/** Body for the AI shot director (see api/shot-prompt.ts). */
export interface ShotPromptVars {
  capability: MediaGenCapability;
  shotMode: ShotMode;
  platform: string;
  aspect?: MediaAspect;
  postType?: string;
  /** The user's rough idea (optional). */
  idea?: string;
  /** Selected product handles to feature (optional — many shots have none). */
  productHandles?: string[];
}

export interface ShotPromptResponse {
  prompt: string;
  negativePrompt: string;
}

/**
 * Ask the AI shot director to write a generation prompt from the brand brain +
 * optional product + optional idea. Synchronous (no job/poll) — returns the
 * prompt + negative prompt for the caller to drop into the form (and optionally
 * generate immediately). Does not mutate server state, so nothing to invalidate.
 */
export function useGenerateShotPrompt(
  wsId: string | undefined,
  brandId: string | undefined,
) {
  return useMutation<ShotPromptResponse, ApiError, ShotPromptVars>({
    mutationFn: (vars) =>
      api<ShotPromptResponse>(`/workspaces/${wsId}/brands/${brandId}/shot-prompt`, {
        method: "POST",
        body: vars,
      }),
  });
}

/**
 * Enqueue a real photo/video generation job. Returns 202 + { jobId } on
 * success; the API returns 402 (payment_required) when the server-side
 * pre-check refuses the spend, which surfaces as an ApiError the caller shows.
 */
export function useCreateMediaGenJob(
  wsId: string | undefined,
  brandId: string | undefined,
) {
  return useMutation<
    { jobId: string; status: MediaGenJobStatus },
    ApiError,
    CreateMediaGenVars
  >({
    mutationFn: (body) =>
      api<{ jobId: string; status: MediaGenJobStatus }>(
        `/workspaces/${wsId}/brands/${brandId}/media-gen`,
        { method: "POST", body },
      ),
  });
}

/**
 * Polls a media-gen job until it's done/failed (mirrors useJob). On completion
 * the caller invalidates the media list so the generated asset surfaces. Stops
 * polling on a hard error or a terminal status.
 */
export function useMediaGenJob(
  wsId: string | undefined,
  brandId: string | undefined,
  jobId: string | undefined,
) {
  return useQuery<MediaGenJobStatusResponse, ApiError>({
    queryKey: ["media-gen-job", wsId, brandId, jobId],
    queryFn: () =>
      api<MediaGenJobStatusResponse>(
        `/workspaces/${wsId}/brands/${brandId}/media-gen/${jobId}`,
      ),
    enabled: !!wsId && !!brandId && !!jobId,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      const status = query.state.data?.status;
      return status === "done" || status === "failed" ? false : 2000;
    },
  });
}

// ─── Media billing state ───────────────────────────────────────────────
//
// GET /workspaces/{ws}/billing — workspace-scoped, read-only. Powers the model
// toggle's honest "Vertex not configured" state (providers.vertex) and the
// billing surface. All money language is ACCRUED/OWED, never "charged".

/** Which media-generation providers this deployment can actually run. */
export interface MediaProviders {
  bedrock: boolean;
  vertex: boolean;
}

/**
 * Self-serve subscription state, denormalized from the workspace row the
 * Stripe webhook maintains. Matches the `subscription` block returned by
 * getBillingHandler exactly: the `subscriptionStatus(...)` result
 * (has/active/locked/plan/cadence/status) plus the display-only period-end /
 * cancel / scheduled-downgrade fields straight off the stored summary.
 * `plan`/`cadence`/`status` are null for a never-subscribed workspace.
 */
export interface SubscriptionBilling {
  has: boolean;
  active: boolean;
  locked: boolean;
  plan: PlanId | null;
  cadence: Cadence | null;
  status: string | null;
  /** ISO timestamp the current paid period ends (renewal / lock boundary). */
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** A scheduled downgrade that takes effect at currentPeriodEnd, if any. */
  scheduledPlan: PlanId | null;
  scheduledCadence: Cadence | null;
}

/** GET /billing response (see api/billing.ts getBillingHandler). */
export interface BillingState {
  plan: PlanId;
  mode: MediaBillingMode;
  allowance: { photo: number; video: number };
  used: { photo: number; video: number };
  remaining: { photo: number; video: number };
  packs: { photo: number; video: number };
  hasPaymentMethod: boolean;
  delinquent: boolean;
  paymentsConfigured: boolean;
  providers: MediaProviders;
  /** Running tab the workspace has ACCRUED and OWES at month-end (cents). */
  accruedCents: number;
  overagePricePerUnit: number;
  /** Self-serve subscription plan/cadence/status + lock + scheduled change. */
  subscription: SubscriptionBilling;
}

export function useBilling(wsId: string | undefined) {
  return useQuery<BillingState, ApiError>({
    queryKey: ["billing", wsId],
    queryFn: () => api<BillingState>(`/workspaces/${wsId}/billing`),
    enabled: !!wsId,
  });
}

/**
 * Begin attaching a payment method. Returns a Stripe SetupIntent client secret
 * the SPA confirms with Stripe Elements. The API returns 503
 * (payments_not_configured) when Stripe isn't set up for the deployment, which
 * surfaces as an ApiError so the UI shows an honest "billing not available".
 */
export function useCreatePaymentMethod(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ clientSecret: string }, ApiError, void>({
    mutationFn: () =>
      api<{ clientSecret: string }>(`/workspaces/${wsId}/billing/payment-method`, {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["billing", wsId] });
    },
  });
}

// ─── Self-serve subscription (subscribe / change-plan / cancel) ─────────
//
// Owner-only (the API re-checks manageWorkspace). The plan + lock live on the
// workspace row too (the webhook denormalizes them), so every mutation here
// invalidates BOTH ["billing", ws] and ["workspace", ws] on success. ApiError
// surfaces the server `message` (no_payment_method / payment_failed /
// plan_limit_exceeded / already_subscribed / contact_sales) for inline display.

/** Plan + cadence the subscribe/change-plan endpoints accept. */
export interface PlanCadence {
  plan: PlanId;
  cadence: Cadence;
}

function invalidateSubscription(
  qc: ReturnType<typeof useQueryClient>,
  wsId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: ["billing", wsId] });
  void qc.invalidateQueries({ queryKey: ["workspace", wsId] });
}

/**
 * Start a paid subscription on the saved card (charges immediately, no trial).
 * POST /billing/subscribe → 202 {ok:true}. ApiError carries the server message
 * for 400 no_payment_method/contact_sales, 402 payment_failed, 409
 * already_subscribed.
 */
export function useSubscribe(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, PlanCadence>({
    mutationFn: (body) =>
      api<{ ok: true }>(`/workspaces/${wsId}/billing/subscribe`, {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidateSubscription(qc, wsId),
  });
}

/**
 * Change an active subscription's plan/cadence. POST /billing/change-plan →
 * 200 on success ({ok, change, …}). Upgrades charge the prorated difference
 * now; downgrades take effect at period end. ApiError carries the server
 * message for 409 plan_limit_exceeded, 402 payment_failed, 400 (enterprise /
 * no-active-sub).
 */
export function useChangePlan(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    {
      ok: true;
      change: "noop" | "upgrade" | "downgrade";
      plan?: PlanId;
      cadence?: Cadence;
      scheduledPlan?: PlanId;
      scheduledCadence?: Cadence;
      effectiveAt?: string | null;
    },
    ApiError,
    PlanCadence
  >({
    mutationFn: (body) =>
      api(`/workspaces/${wsId}/billing/change-plan`, {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidateSubscription(qc, wsId),
  });
}

/**
 * Cancel the active subscription at period end. POST /billing/cancel → 200
 * {ok:true, endsAt}. The workspace keeps access until endsAt, then locks until
 * it re-subscribes.
 */
export function useCancelSubscription(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true; endsAt: string | null }, ApiError, void>({
    mutationFn: () =>
      api<{ ok: true; endsAt: string | null }>(`/workspaces/${wsId}/billing/cancel`, {
        method: "POST",
      }),
    onSuccess: () => invalidateSubscription(qc, wsId),
  });
}

// ─── Inputs feed ──────────────────────────────────────────────────────

export type { InputRecord } from "@notamen/core/db";

/** Derive the input id from a record's `sk` (`INPUT#<id>`). */
export function inputId(record: InputRecord): string {
  return record.sk.replace(/^INPUT#/, "");
}

export function useInputs(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<{ inputs: InputRecord[] }, ApiError>({
    queryKey: ["inputs", wsId, brandId],
    queryFn: () => api<{ inputs: InputRecord[] }>(`/workspaces/${wsId}/brands/${brandId}/inputs`),
    enabled: !!wsId && !!brandId,
  });
}

export interface CreateInputVars {
  text: string;
  sourceUrl?: string;
  notes?: string;
}

export function useCreateInput(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ input: InputRecord }, ApiError, CreateInputVars>({
    mutationFn: (body) =>
      api<{ input: InputRecord }>(`/workspaces/${wsId}/brands/${brandId}/inputs`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inputs", wsId, brandId] });
    },
  });
}

export function useDeleteInput(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      api<{ ok: true }>(`/workspaces/${wsId}/brands/${brandId}/inputs/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inputs", wsId, brandId] });
    },
  });
}

// ─── Draft generation ─────────────────────────────────────────────────

export type { DraftRecord, GenerationJobRecord, DraftProvider } from "@notamen/core/db";

export interface GenerateRequest {
  input: { text: string; notes?: string };
  platforms: Platform[];
  kinds: ContentKind[];
  providers: DraftProvider[];
}

export function useCreateDraftJob(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ jobId: string; status: string }, ApiError, GenerateRequest>({
    mutationFn: (body) =>
      api<{ jobId: string; status: string }>(
        `/workspaces/${wsId}/brands/${brandId}/drafts`,
        { method: "POST", body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["drafts", wsId, brandId] });
    },
  });
}

/**
 * Polls a generation job until it's done/failed. `refetchInterval`
 * returns false once terminal so polling stops.
 */
export function useJob(
  wsId: string | undefined,
  brandId: string | undefined,
  jobId: string | undefined,
) {
  return useQuery<{ job: GenerationJobRecord }, ApiError>({
    queryKey: ["job", wsId, brandId, jobId],
    queryFn: () =>
      api<{ job: GenerationJobRecord }>(`/workspaces/${wsId}/brands/${brandId}/jobs/${jobId}`),
    enabled: !!wsId && !!brandId && !!jobId,
    refetchInterval: (query) => {
      // Stop polling on a hard error (e.g. job 404) so we don't hammer a
      // broken endpoint forever, and on terminal job states.
      if (query.state.status === "error") return false;
      const status = query.state.data?.job.status;
      return status === "done" || status === "failed" ? false : 2000;
    },
  });
}

export function useDrafts(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<{ drafts: DraftRecord[] }, ApiError>({
    queryKey: ["drafts", wsId, brandId],
    queryFn: () => api<{ drafts: DraftRecord[] }>(`/workspaces/${wsId}/brands/${brandId}/drafts`),
    enabled: !!wsId && !!brandId,
  });
}

// ─── Whole-post studio (candidate batches → assemble draft) ────────────
//
// POST generate-post-candidates enqueues ONE brief that drives BOTH caption
// candidates and image-generation jobs, returning a batch. GET post-candidates
// returns the most-recent retained batches (image jobs resolved to mediaIds as
// they finish). POST drafts/assemble turns a chosen caption + image into a real
// DraftRecord in the queue. The list query live-polls while any image job is
// still queued/running so generating tiles flip to the rendered image on their
// own.

export interface PostCandidateCaption {
  body: string;
  hook?: string;
  hashtags?: string[];
  callToAction?: string;
  imageBrief?: string;
}
export interface PostCandidateImage {
  jobId: string;
  status: string;
  mediaIds: string[];
  error?: string;
}
export interface PostCandidateBatch {
  batchId: string;
  createdAt: string;
  brief: string;
  platform: string;
  postType?: string;
  aspect?: MediaAspect;
  count: number;
  provider: string;
  captions: PostCandidateCaption[];
  images: PostCandidateImage[];
}
export interface GeneratePostRequest {
  brief: string;
  platform: string;
  count: number;
  modelId: string;
  shotMode?: string;
  aspect?: string;
  postType?: string;
  productHandles?: string[];
}
export interface AssembleDraftRequest {
  batchId: string;
  platform: string;
  caption: PostCandidateCaption;
  mediaIds: string[];
  aspect?: string;
  mediaType?: string;
}

export function useGeneratePostCandidates(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ batch: PostCandidateBatch }, ApiError, GeneratePostRequest>({
    mutationFn: (body) =>
      api<{ batch: PostCandidateBatch }>(
        `/workspaces/${wsId}/brands/${brandId}/generate-post-candidates`,
        { method: "POST", body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["post-candidates", wsId, brandId] });
    },
  });
}

export function usePostCandidates(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<{ batches: PostCandidateBatch[]; conflicts?: string[] }, ApiError>({
    queryKey: ["post-candidates", wsId, brandId],
    queryFn: () =>
      api<{ batches: PostCandidateBatch[]; conflicts?: string[] }>(
        `/workspaces/${wsId}/brands/${brandId}/post-candidates`,
      ),
    enabled: !!wsId && !!brandId,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      const batches = query.state.data?.batches ?? [];
      const anyPending = batches.some((b) =>
        b.images.some((im) => im.status === "queued" || im.status === "running"),
      );
      return anyPending ? 2000 : false;
    },
  });
}

export function useAssembleDraft(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ draft: unknown }, ApiError, AssembleDraftRequest>({
    mutationFn: (body) =>
      api<{ draft: unknown }>(`/workspaces/${wsId}/brands/${brandId}/drafts/assemble`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["drafts", wsId, brandId] });
    },
  });
}

// ─── Feedback (👍/👎 + reason on candidates and queue rejects) ──────────
// Captures one reaction. A dislike must carry ≥1 reason; a like sends
// reasons:[]. No cache invalidation yet — nothing displays feedback until 3d.
export interface SubmitFeedbackRequest {
  target: "caption" | "image";
  sentiment: "like" | "dislike";
  reasons: string[];
  source: "studio" | "queue";
  batchId?: string;
  draftId?: string;
  captionIdx?: number;
  mediaId?: string;
  platform?: string;
  postType?: string;
  captionText?: string;
}

export function useSubmitFeedback(wsId: string | undefined, brandId: string | undefined) {
  return useMutation<{ feedback: unknown }, ApiError, SubmitFeedbackRequest>({
    mutationFn: (body) =>
      api<{ feedback: unknown }>(`/workspaces/${wsId}/brands/${brandId}/feedback`, {
        method: "POST",
        body,
      }),
  });
}

// ─── Draft lifecycle actions (approve / reject / schedule) ─────────────

const draftActionBase = (wsId: string | undefined, brandId: string | undefined, draftId: string) =>
  `/workspaces/${wsId}/brands/${brandId}/drafts/${draftId}`;

export interface ApproveVars {
  draftId: string;
  /** Required to approve a draft carrying a block-level warning. */
  override?: boolean;
  overrideReason?: string;
}

export function useApproveDraft(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ draft: DraftRecord }, ApiError, ApproveVars>({
    mutationFn: ({ draftId, override, overrideReason }) =>
      api<{ draft: DraftRecord }>(`${draftActionBase(wsId, brandId, draftId)}/approve`, {
        method: "POST",
        body: {
          ...(override ? { override: true } : {}),
          ...(overrideReason ? { overrideReason } : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["drafts", wsId, brandId] });
    },
  });
}

export function useRejectDraft(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ draft: DraftRecord }, ApiError, { draftId: string; reason?: string }>({
    mutationFn: ({ draftId, reason }) =>
      api<{ draft: DraftRecord }>(`${draftActionBase(wsId, brandId, draftId)}/reject`, {
        method: "POST",
        body: reason ? { reason } : {},
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["drafts", wsId, brandId] });
    },
  });
}

export function useScheduleDraft(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ draft: DraftRecord }, ApiError, { draftId: string; scheduledFor: string }>({
    mutationFn: ({ draftId, scheduledFor }) =>
      api<{ draft: DraftRecord }>(`${draftActionBase(wsId, brandId, draftId)}/schedule`, {
        method: "POST",
        body: { scheduledFor },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["drafts", wsId, brandId] });
    },
  });
}

export function useUnscheduleDraft(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ draft: DraftRecord }, ApiError, { draftId: string }>({
    mutationFn: ({ draftId }) =>
      api<{ draft: DraftRecord }>(`${draftActionBase(wsId, brandId, draftId)}/unschedule`, {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["drafts", wsId, brandId] });
    },
  });
}

/** Per-variant text edits, sent by index. Structural fields stay server-side. */
export interface EditVariantInput {
  body?: string;
  hashtags?: string[];
  hook?: string;
  imageBrief?: string;
  callToAction?: string;
  notes?: string;
}

export function useEditDraft(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { draft: DraftRecord },
    ApiError,
    { draftId: string; variants: EditVariantInput[] }
  >({
    mutationFn: ({ draftId, variants }) =>
      api<{ draft: DraftRecord }>(`${draftActionBase(wsId, brandId, draftId)}/edit`, {
        method: "POST",
        body: { variants },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["drafts", wsId, brandId] });
    },
  });
}

// ─── Draft comments ───────────────────────────────────────────────────

export interface DraftComment {
  id: string;
  draftId: string;
  authorSub: string;
  authorEmail: string;
  authorRole: string;
  body: string;
  createdAt: string;
}

export function useComments(
  wsId: string | undefined,
  brandId: string | undefined,
  draftId: string | undefined,
) {
  return useQuery<{ comments: DraftComment[] }, ApiError>({
    queryKey: ["comments", wsId, brandId, draftId],
    queryFn: () =>
      api<{ comments: DraftComment[] }>(
        `/workspaces/${wsId}/brands/${brandId}/drafts/${draftId}/comments`,
      ),
    enabled: !!wsId && !!brandId && !!draftId,
  });
}

export function useAddComment(
  wsId: string | undefined,
  brandId: string | undefined,
  draftId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation<{ comment: DraftComment }, ApiError, { body: string }>({
    mutationFn: ({ body }) =>
      api<{ comment: DraftComment }>(
        `/workspaces/${wsId}/brands/${brandId}/drafts/${draftId}/comments`,
        { method: "POST", body: { body } },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["comments", wsId, brandId, draftId] });
    },
  });
}

// ─── Brand learning (scan) ────────────────────────────────────────────

export type { BrandScanJobRecord } from "@notamen/core/db";

export interface ScanRequest {
  urls?: string[];
  pastedText?: string;
}

export function useCreateScanJob(wsId: string | undefined, brandId: string | undefined) {
  return useMutation<{ jobId: string; status: string }, ApiError, ScanRequest>({
    mutationFn: (body) =>
      api<{ jobId: string; status: string }>(`/workspaces/${wsId}/brands/${brandId}/scan`, {
        method: "POST",
        body,
      }),
  });
}

export function useScanJob(
  wsId: string | undefined,
  brandId: string | undefined,
  jobId: string | undefined,
) {
  return useQuery<{ job: BrandScanJobRecord }, ApiError>({
    queryKey: ["scan-job", wsId, brandId, jobId],
    queryFn: () =>
      api<{ job: BrandScanJobRecord }>(`/workspaces/${wsId}/brands/${brandId}/scan-jobs/${jobId}`),
    enabled: !!wsId && !!brandId && !!jobId,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      const status = query.state.data?.job.status;
      return status === "done" || status === "failed" ? false : 2000;
    },
  });
}

// ─── Trends ───────────────────────────────────────────────────────────

export type {
  TrendClusterRecord,
  TrendConfigRecord,
  TrendDigestRecord,
  TrendScanJobRecord,
  TrendSourceAccountRecord,
  TrendTopicRecord,
} from "@notamen/core/db";
export type { TrendSourceConfig, TrendSourceKind } from "@notamen/core/types";

const trendsBase = (wsId: string | undefined, brandId: string | undefined) =>
  `/workspaces/${wsId}/brands/${brandId}/trends`;

export function useTrendConfig(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<{ config: TrendConfigRecord }, ApiError>({
    queryKey: ["trend-config", wsId, brandId],
    queryFn: () => api<{ config: TrendConfigRecord }>(`${trendsBase(wsId, brandId)}/config`),
    enabled: !!wsId && !!brandId,
  });
}

export interface UpdateTrendConfigVars {
  cadence: TrendConfigRecord["cadence"];
  autoPushToBrain: boolean;
  minRelevance: number;
  watchlistKeywords?: string[];
  digestEnabled?: boolean;
}

export function useUpdateTrendConfig(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ config: TrendConfigRecord }, ApiError, UpdateTrendConfigVars>({
    mutationFn: (body) =>
      api<{ config: TrendConfigRecord }>(`${trendsBase(wsId, brandId)}/config`, {
        method: "PUT",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["trend-config", wsId, brandId], data);
    },
  });
}

/**
 * Connect a Slack Incoming Webhook for this brand's daily digest (admin only,
 * enforced server-side). The webhook URL is stored as a secret server-side;
 * the returned config carries only `digestSlackWebhookSsmKey` (presence =
 * connected), never the URL.
 */
export function useConnectDigestSlack(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ config: TrendConfigRecord }, ApiError, { webhookUrl: string }>({
    mutationFn: (body) =>
      api<{ config: TrendConfigRecord }>(`${trendsBase(wsId, brandId)}/digest/slack`, {
        method: "POST",
        body,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["trend-config", wsId, brandId], data);
    },
  });
}

export function useDisconnectDigestSlack(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ config: TrendConfigRecord | null }, ApiError, void>({
    mutationFn: () =>
      api<{ config: TrendConfigRecord | null }>(`${trendsBase(wsId, brandId)}/digest/slack`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["trend-config", wsId, brandId] });
    },
  });
}

export function useTrendSources(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<{ sources: TrendSourceAccountRecord[] }, ApiError>({
    queryKey: ["trend-sources", wsId, brandId],
    queryFn: () =>
      api<{ sources: TrendSourceAccountRecord[] }>(`${trendsBase(wsId, brandId)}/sources`),
    enabled: !!wsId && !!brandId,
  });
}

export interface ConnectTrendSourceVars {
  kind: TrendSourceKind;
  config: TrendSourceConfig;
  label?: string;
}

export function useConnectTrendSource(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ source: TrendSourceAccountRecord }, ApiError, ConnectTrendSourceVars>({
    mutationFn: (body) =>
      api<{ source: TrendSourceAccountRecord }>(`${trendsBase(wsId, brandId)}/sources`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["trend-sources", wsId, brandId] });
    },
  });
}

export function useDisconnectTrendSource(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { sourceId: string }>({
    mutationFn: ({ sourceId }) =>
      api<{ ok: true }>(`${trendsBase(wsId, brandId)}/sources/${sourceId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["trend-sources", wsId, brandId] });
    },
  });
}

export function useTrendClusters(
  wsId: string | undefined,
  brandId: string | undefined,
  status?: TrendClusterRecord["status"],
) {
  return useQuery<{ clusters: TrendClusterRecord[] }, ApiError>({
    queryKey: ["trend-clusters", wsId, brandId, status ?? "all"],
    queryFn: () =>
      api<{ clusters: TrendClusterRecord[] }>(
        `${trendsBase(wsId, brandId)}/clusters${status ? `?status=${status}` : ""}`,
      ),
    enabled: !!wsId && !!brandId,
  });
}

export function useTrendCluster(
  wsId: string | undefined,
  brandId: string | undefined,
  clusterId: string | undefined,
) {
  return useQuery<{ cluster: TrendClusterRecord; members: TrendTopicRecord[] }, ApiError>({
    queryKey: ["trend-cluster", wsId, brandId, clusterId],
    queryFn: () =>
      api<{ cluster: TrendClusterRecord; members: TrendTopicRecord[] }>(
        `${trendsBase(wsId, brandId)}/clusters/${clusterId}`,
      ),
    enabled: !!wsId && !!brandId && !!clusterId,
  });
}

/** Invalidate the cluster list + a single cluster after a status change. */
function invalidateClusters(
  qc: ReturnType<typeof useQueryClient>,
  wsId: string | undefined,
  brandId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: ["trend-clusters", wsId, brandId] });
  void qc.invalidateQueries({ queryKey: ["trend-cluster", wsId, brandId] });
}

export function usePushClusterToBrain(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ cluster: TrendClusterRecord }, ApiError, { clusterId: string }>({
    mutationFn: ({ clusterId }) =>
      api<{ cluster: TrendClusterRecord }>(
        `${trendsBase(wsId, brandId)}/clusters/${clusterId}/push-to-brain`,
        { method: "POST" },
      ),
    onSuccess: () => invalidateClusters(qc, wsId, brandId),
  });
}

export interface GenerateFromClusterVars {
  clusterId: string;
  platforms: Platform[];
  kinds: ContentKind[];
  providers: DraftProvider[];
}

export function useGenerateFromCluster(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { jobId: string; status: string; cluster: TrendClusterRecord },
    ApiError,
    GenerateFromClusterVars
  >({
    mutationFn: ({ clusterId, ...body }) =>
      api<{ jobId: string; status: string; cluster: TrendClusterRecord }>(
        `${trendsBase(wsId, brandId)}/clusters/${clusterId}/generate`,
        { method: "POST", body },
      ),
    onSuccess: () => invalidateClusters(qc, wsId, brandId),
  });
}

export function useRescanTrends(wsId: string | undefined, brandId: string | undefined) {
  return useMutation<{ job: TrendScanJobRecord }, ApiError, { sourceKinds?: TrendSourceKind[] }>({
    mutationFn: (body) =>
      api<{ job: TrendScanJobRecord }>(`${trendsBase(wsId, brandId)}/rescan`, {
        method: "POST",
        body: body.sourceKinds?.length ? { sourceKinds: body.sourceKinds } : {},
      }),
  });
}

export function useTrendScanJob(
  wsId: string | undefined,
  brandId: string | undefined,
  jobId: string | undefined,
) {
  const qc = useQueryClient();
  return useQuery<{ job: TrendScanJobRecord }, ApiError>({
    queryKey: ["trend-scan-job", wsId, brandId, jobId],
    queryFn: () =>
      api<{ job: TrendScanJobRecord }>(`${trendsBase(wsId, brandId)}/scans/${jobId}`),
    enabled: !!wsId && !!brandId && !!jobId,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      const status = query.state.data?.job.status;
      if (status === "done" || status === "failed") {
        // Scan finished — surface fresh clusters/sources without a reload.
        void qc.invalidateQueries({ queryKey: ["trend-clusters", wsId, brandId] });
        void qc.invalidateQueries({ queryKey: ["trend-sources", wsId, brandId] });
        return false;
      }
      return 2500;
    },
  });
}

// ─── Trend digests (in-app daily digest) ──────────────────────────────

/**
 * All in-app digests for a brand (most recent first), built by the
 * daily-digest worker. The nav badge derives its unread count from this same
 * cached query (digests with no `readAt`), so it stays one lightweight fetch.
 */
export function useTrendDigests(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<{ digests: TrendDigestRecord[] }, ApiError>({
    queryKey: ["trend-digests", wsId, brandId],
    queryFn: () => api<{ digests: TrendDigestRecord[] }>(`${trendsBase(wsId, brandId)}/digests`),
    enabled: !!wsId && !!brandId,
  });
}

export function useTrendDigest(
  wsId: string | undefined,
  brandId: string | undefined,
  digestId: string | undefined,
) {
  return useQuery<{ digest: TrendDigestRecord }, ApiError>({
    queryKey: ["trend-digest", wsId, brandId, digestId],
    queryFn: () =>
      api<{ digest: TrendDigestRecord }>(`${trendsBase(wsId, brandId)}/digests/${digestId}`),
    enabled: !!wsId && !!brandId && !!digestId,
  });
}

export function useMarkDigestRead(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ digest: TrendDigestRecord }, ApiError, { digestId: string }>({
    mutationFn: ({ digestId }) =>
      api<{ digest: TrendDigestRecord }>(
        `${trendsBase(wsId, brandId)}/digests/${digestId}/read`,
        { method: "POST" },
      ),
    onSuccess: () => {
      // Refresh the list (clears the unread badge) and the single digest.
      void qc.invalidateQueries({ queryKey: ["trend-digests", wsId, brandId] });
      void qc.invalidateQueries({ queryKey: ["trend-digest", wsId, brandId] });
    },
  });
}

// ─── Learning (feedback-only engine, Stage F) ───────────────────────────
//
// Wires the Learning surface to the feedback-driven backend that shipped in
// PRs F1–F2: proposed brain edits derived from durable reviewer feedback,
// the accept/snooze/reject lifecycle, and the brain version history the
// Brain-diff tab renders. The post-publish metrics engine (Posts/Forecast
// tabs, engagement-impact deltas) is a later phase and not wired here.

export type { PatternRecord, BrandVersionRecord, PatternStatus } from "@notamen/core/db";

/** Per-status counts returned alongside the pattern list. */
export type PatternSummary = Record<PatternStatus, number>;

const learningBase = (wsId: string | undefined, brandId: string | undefined) =>
  `/workspaces/${wsId}/brands/${brandId}/learning`;

/** GET .../learning — all patterns (recent-first) + a per-status summary. */
export function useLearning(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<{ patterns: PatternRecord[]; summary: PatternSummary }, ApiError>({
    queryKey: ["learning", wsId, brandId],
    queryFn: () =>
      api<{ patterns: PatternRecord[]; summary: PatternSummary }>(learningBase(wsId, brandId)),
    enabled: !!wsId && !!brandId,
  });
}

/** GET .../learning/versions — brain version history for the Brain-diff tab. */
export function useBrainVersions(wsId: string | undefined, brandId: string | undefined) {
  return useQuery<{ versions: BrandVersionRecord[] }, ApiError>({
    queryKey: ["brain-versions", wsId, brandId],
    queryFn: () =>
      api<{ versions: BrandVersionRecord[] }>(`${learningBase(wsId, brandId)}/versions`),
    enabled: !!wsId && !!brandId,
  });
}

/** POST .../learning/analyze — (re)derive patterns from recent feedback. */
export function useAnalyzeLearning(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ patterns: PatternRecord[]; summary: PatternSummary }, ApiError, void>({
    mutationFn: () =>
      api<{ patterns: PatternRecord[]; summary: PatternSummary }>(
        `${learningBase(wsId, brandId)}/analyze`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["learning", wsId, brandId] });
    },
  });
}

export type PatternDecision = "accept" | "snooze" | "reject";

export interface DecidePatternVars {
  patternId: string;
  op: PatternDecision;
  /** Optional free-text reason, stored on snooze / reject. */
  reason?: string;
}

/** POST .../learning/{patternId}/{accept|snooze|reject}. Accept also writes a
 *  brain version, so it invalidates the version history too. */
export function useDecidePattern(wsId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { pattern: PatternRecord; version?: BrandVersionRecord },
    ApiError,
    DecidePatternVars
  >({
    mutationFn: ({ patternId, op, reason }) =>
      api<{ pattern: PatternRecord; version?: BrandVersionRecord }>(
        `${learningBase(wsId, brandId)}/${patternId}/${op}`,
        {
          method: "POST",
          ...(reason && reason.trim() ? { body: { reason: reason.trim() } } : {}),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["learning", wsId, brandId] });
      void qc.invalidateQueries({ queryKey: ["brain-versions", wsId, brandId] });
    },
  });
}
